import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "reactstrap";
import { Button, Nav, Tab } from "react-bootstrap";
import SikkaInput from "../../common/SikkaInput";
import { useForm } from "react-hook-form";
import CreditPaymentForm from "./CreditPaymentForm";
import creditCard from "../../../assets/img/icons/icon-payment-methods.png";
import { toast } from "react-toastify";
import { useLocation } from "react-router";
import AuthService from "../../../services/authenticationService";
import { CapitalizeString } from "./RegisterPayment";


const DirectPayment = ({
  step,                  
  setStep,                
  registerFormValues = {},
  setPaymentFormValues,   
  paymentFormValues = {},
}) => {
  const defaultKey = "credit";
  const [defaultActiveKey, setDefaultActiveKey] = useState(defaultKey);

  const [formValues, setFormValues] = useState(paymentFormValues);
  const creditCardRef = useRef();
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);

  const {
    register,
    errors,
    watch,
    handleSubmit,
    reset,
    control,
    setValue,
    setError,
    clearError,
  } = useForm();

  const useQuery = () => {
      const { search } = useLocation();

      return React.useMemo(() => new URLSearchParams(search), [search]);
    };
  
  const query = useQuery();

  const projectId = query.get("project_id") || "";
  const redirectUrl = query.get("redirect_url") || "";
  const callbackKey = query.get("callback_key") || "";

  const outSideFields = ["address", "zip", "state", "city", "email_id", "first_name", "last_name"];

  // Prefill from previously cached values
  useEffect(() => {
    const getKeys = formValues[defaultActiveKey]
      ? Object.keys(formValues[defaultActiveKey]).filter((k) => k.includes(defaultActiveKey))
      : [];

    let paySubset = {};
    getKeys.forEach((k) => {
      paySubset = { ...paySubset, [k]: paymentFormValues?.[defaultActiveKey]?.[k] };
    });

    let regionSubset = {};
    outSideFields.forEach((k) => {
      regionSubset = { ...regionSubset, [k]: paymentFormValues?.[k] };
    });

    const combined = { ...paySubset, ...regionSubset };
    reset(combined);
  }, [defaultActiveKey]);

  const watchLiveData = watch();
  const liveWatch = JSON.stringify(Object.values(watchLiveData));

  useEffect(() => {
    let regionSubset = {};
    outSideFields.forEach((k) => {
      regionSubset = { ...regionSubset, [k]: watchLiveData[k] };
    });

    const next = {
      ...paymentFormValues,
      [defaultActiveKey]: watchLiveData,
      last_default_key: defaultActiveKey,
      ...regionSubset,
    };

    setPaymentFormValues?.(next);
    setFormValues({
      ...formValues,
      [defaultActiveKey]: watchLiveData,
      last_default_key: defaultActiveKey,
    });
  }, [liveWatch]);

  const handleDirectPaymentSubmit = (values) => {
    const body = {
      project_id: projectId,
      redirect_url: redirectUrl,
      callback_key: callbackKey,
      card_details: {
        type_of_payment: defaultActiveKey,
        firstname: values.first_name,
        lastname: values.last_name,
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        email_id: (values.email_id || registerFormValues?.username || "").toLowerCase(),
      },
    };

    if (defaultActiveKey === "credit") {
      const dateValue = values["credit_card_expiration_date"]?.split("/") || [];
      const monthValue = dateValue[0] || "";
      const yearValue = dateValue[1] || "";

      body.card_details.card_number = values.credit_card_number;
      body.card_details.expiration_date = `${yearValue}-${monthValue}`;
      body.card_details.cvv = values.credit_security_code;
    }


    setIsSubmitButtonClicked(true);

    
    AuthService.register("pay_for_download_code_sai", body).then(
      (res) => {
        toast.success(res?.data?.message || "Payment successful.");
        setIsSubmitButtonClicked(false);
        setTimeout(() => {
          if (res?.data?.items?.url) {
            window.location.href = res?.data?.items?.url;
          }
        }, 2000);
      },
      (error) => {
        const resMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();

        setIsSubmitButtonClicked(false);
        if (resMessage.includes("A duplicate transaction has been submitted")) {
          toast.error("A duplicate transaction has been submitted. Please try again after sometime.");
        } else {
          toast.error(resMessage);
        }
      }
    );
  };

  return (
    <>

      {/* Payment method tabs */}
      <div className="payment-types">
        <Tab.Container
          defaultActiveKey={defaultActiveKey}
          onSelect={(selectedValue) => setDefaultActiveKey(selectedValue)}
        >
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link eventKey="credit" className="fs--1 px-2 px-md-3 cursor-pointer outline-none">
                Credit Card
              </Nav.Link>
            </Nav.Item>

          </Nav>

          <Row className="mb-2 mt-2">
            <Col className="d-flex justify-content-center">
              <img src={creditCard} height="20" alt="payment-methods" width="auto" />
            </Col>
          </Row>

          <Tab.Content>
            <Tab.Pane eventKey="credit">
              {defaultActiveKey === "credit" && (
                <CreditPaymentForm
                  setFormValue={setFormValues}
                  register={register}
                  errors={errors}
                  watch={watch}
                  handleSubmit={handleSubmit}
                  reset={reset}
                  control={control}
                  setValue={setValue}
                  formRef={creditCardRef}
                  form_name={defaultActiveKey}
                  setError={setError}
                  clearError={clearError}
                  currentFormValue={paymentFormValues[defaultActiveKey]}
                />
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      <div className="billing-information-container">
        <Row className="mt-1 mb-1">
          <Col>
            <h6>Billing Information</h6>
          </Col>
        </Row>

        <Row>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="First Name*"
              placeholder="First Name"
              id="first_name"
              name="first_name"
              innerRef={register({ required: "First Name is required" })}
              errors={errors}
            />
          </Col>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="Last Name*"
              placeholder="Last Name"
              id="last_name"
              name="last_name"
              innerRef={register({ required: "Last Name is required" })}
              errors={errors}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="Address*"
              placeholder="Address"
              id="address"
              name="address"
              innerRef={register({ required: "Address is required" })}
              errors={errors}
            />
          </Col>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="City*"
              placeholder="City"
              id="city"
              name="city"
              innerRef={register({ required: "City is required" })}
              errors={errors}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="State*"
              placeholder="State"
              id="state"
              name="state"
              innerRef={register({ required: "State is required" })}
              errors={errors}
            />
          </Col>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="Zip*"
              placeholder="Zip"
              id="zip"
              name="zip"
              innerRef={register({ required: "Zip is required" })}
              errors={errors}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <SikkaInput
              onBlur={(e) => {
                if (!e.target.value.trim()) {
                  setError(e.target.name, "custom", `${CapitalizeString(e.target.name)} field should not have whitespace as value.`);
                } else {
                  clearError(e.target.name);
                  setValue(e.target.name, e.target.value.trim());
                }
              }}
              label="Email Id*"
              placeholder="Email ID"
              id="email_id"
              name="email_id"
              innerRef={register({
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                  message: "Email must be valid",
                },
              })}
              errors={errors}
            />
          </Col>
        </Row>
      </div>


      <div
        className="mt-2"
        style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
      >
        <Button
          variant="primary"
          size="sm"
          className="px-4"
          form={`${defaultActiveKey}-hook-form`}
          onClick={handleSubmit(handleDirectPaymentSubmit)}
          disabled={isSubmitButtonClicked}
        >
          Pay & Submit
        </Button>
      </div>
    </>
  );
};

export default DirectPayment;